"use client"

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import type { HeadingItem } from '@/lib/utils/extract-headings'

interface TableOfContentsProps {
  headings: HeadingItem[]
  articleId: string
  description?: string
  topMeta?: React.ReactNode
  bottomMeta?: React.ReactNode
}

export function TableOfContents({ headings, articleId, description, topMeta, bottomMeta }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('')

  useEffect(() => {
    if (headings.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

        if (intersecting.length > 0) {
          setActiveId(intersecting[0].target.id)
        }
      },
      { rootMargin: '-80px 0% -60% 0%', threshold: 0 },
    )

    for (const { id } of headings) {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    }

    return () => observer.disconnect()
  }, [headings])

  if (headings.length === 0) return null

  const hasLeft = description || topMeta || bottomMeta

  return (
    <nav
      aria-label="Table of contents"
      className="rounded-lg border bg-muted/40 mb-8 overflow-hidden flex flex-col"
    >
      <div className="grid grid-cols-[3fr_2fr] divide-x">
        {hasLeft && (
          <div className="px-5 py-4 flex flex-col gap-3 justify-center">
            {topMeta}
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
            {bottomMeta}
          </div>
        )}
        <div className={cn('px-5 py-4', !hasLeft && 'col-span-2')}>
          <p className="text-sm font-semibold mb-3 text-foreground text-right">On this page</p>
          <ul className="space-y-1.5">
            {headings.map(({ id, text, level }) => (
              <li
                key={id}
                className={cn(
                  'text-right',
                  level === 3 && 'pr-3',
                  level === 4 && 'pr-6',
                )}
              >
                <a
                  href={`#${id}`}
                  data-faro-user-action-name={`toc-${articleId}-${id}`}
                  aria-current={activeId === id ? 'location' : undefined}
                  className={cn(
                    'text-sm transition-colors hover:text-foreground',
                    activeId === id
                      ? 'text-foreground font-medium'
                      : 'text-muted-foreground',
                  )}
                >
                  {text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </nav>
  )
}
